import type { InfiniteData, QueryClient } from "@tanstack/react-query";
import type { MailboxSummary, MailboxThread, MailboxThreadsResponse, MailboxView, MailLabel, Message, Paged } from "./api";
import { MAILBOX_LABELS_QUERY_KEY, MAILBOX_SUMMARY_QUERY_KEY, MAILBOX_THREADS_QUERY_KEY, MESSAGES_QUERY_KEY } from "./query-keys";

export type MailboxStateOperation = "archive" | "unarchive" | "trash" | "restore" | "read" | "unread" | "star" | "unstar";

function patchThread(thread: MailboxThread, operation: MailboxStateOperation): MailboxThread {
    if (operation === "archive") return { ...thread, archived: true };
    if (operation === "unarchive") return { ...thread, archived: false };
    if (operation === "trash") return { ...thread, trashed: true };
    if (operation === "restore") return { ...thread, trashed: false };
    if (operation === "read") return { ...thread, unread: false };
    if (operation === "unread") return { ...thread, unread: true };
    if (operation === "star") return { ...thread, starred: true };
    return { ...thread, starred: false };
}

function belongsInView(thread: MailboxThread, view: MailboxView): boolean {
    if (view === "inbox") return Boolean(thread.latestInboundAt) && !thread.archived && !thread.trashed;
    if (view === "sent") return Boolean(thread.latestOutboundAt) && !thread.trashed;
    if (view === "starred") return thread.starred && !thread.trashed;
    if (view === "unread") return thread.unread && !thread.trashed;
    if (view === "archive") return thread.archived && !thread.trashed;
    if (view === "trash") return thread.trashed;
    return !thread.trashed;
}

function messageActivityAt(message: Message): string {
    return message.receivedAt ?? message.sentAt ?? message.createdAt;
}

function mergeMessages(current: readonly Message[], incoming: readonly Message[]): Message[] {
    const byId = new Map(current.map(message => [message.id, message]));
    // Merge, don't replace: realtime rows are partial (no bodyHtml) and must
    // not erase a body we already hold.
    for (const message of incoming) byId.set(message.id, { ...byId.get(message.id), ...message } as Message);
    return [...byId.values()].sort((left, right) =>
        messageActivityAt(right).localeCompare(messageActivityAt(left)) || right.id.localeCompare(left.id),
    );
}

function projectImportedThread(
    existing: MailboxThread | undefined,
    messages: readonly Message[],
    fresh: readonly Message[],
): { thread: MailboxThread; newInbound: boolean } {
    const latestMessage = mergeMessages(existing ? [existing.latestMessage] : [], messages)[0];
    const inboundTimes = messages.filter(message => message.direction === "inbound").map(messageActivityAt);
    const outboundTimes = messages.filter(message => message.direction === "outbound").map(messageActivityAt);
    // Same rules as the server (lib/mailbox.ts): inbound mail newer than the
    // thread's last inbound makes it unread and pulls it out of the archive.
    const newInbound = fresh.some(message => message.direction === "inbound"
        && (!existing?.latestInboundAt || messageActivityAt(message) > existing.latestInboundAt));
    return {
        newInbound,
        thread: {
            threadKey: latestMessage.threadKey,
            activityAt: messageActivityAt(latestMessage),
            latestInboundAt: [...(existing?.latestInboundAt ? [existing.latestInboundAt] : []), ...inboundTimes].sort().at(-1) ?? null,
            latestOutboundAt: [...(existing?.latestOutboundAt ? [existing.latestOutboundAt] : []), ...outboundTimes].sort().at(-1) ?? null,
            // Only messages this cache hasn't seen count — a realtime echo of
            // an optimistic send (or a partial row followed by its full row)
            // must not double-count.
            messageCount: (existing?.messageCount ?? 0) + fresh.length,
            unread: (existing?.unread ?? false) || newInbound,
            archived: (existing?.archived ?? false) && !newInbound,
            trashed: existing?.trashed ?? false,
            starred: existing?.starred ?? false,
            labels: existing?.labels ?? [],
            latestMessage,
        },
    };
}

/** Every message id currently cached anywhere for `threadKey`. */
function knownMessageIds(queryClient: QueryClient, threadKey: string): Set<string> {
    const ids = new Set<string>();
    for (const message of queryClient.getQueryData<Message[]>(MESSAGES_QUERY_KEY) ?? []) ids.add(message.id);
    const thread = queryClient.getQueryData<InfiniteData<Paged<Message>>>([...MESSAGES_QUERY_KEY, "thread", threadKey]);
    for (const page of thread?.pages ?? []) for (const message of page.data) ids.add(message.id);
    for (const [, current] of queryClient.getQueriesData<InfiniteData<MailboxThreadsResponse>>({ queryKey: MAILBOX_THREADS_QUERY_KEY })) {
        for (const page of current?.pages ?? []) {
            for (const t of page.threads) if (t.threadKey === threadKey) ids.add(t.latestMessage.id);
        }
    }
    return ids;
}

function matchesLoadedMailbox(
    queryKey: readonly unknown[],
    thread: MailboxThread,
    alreadyPresent: boolean,
): boolean {
    const view = queryKey[2] as MailboxView | undefined;
    if (!view || !belongsInView(thread, view)) return false;
    if (view === "label" && !alreadyPresent) return false;

    const search = typeof queryKey[4] === "string" ? queryKey[4].trim().toLocaleLowerCase() : "";
    if (search && !alreadyPresent) {
        const searchable = [
            thread.latestMessage.subject,
            thread.latestMessage.fromAddress,
            thread.latestMessage.fromName,
            thread.latestMessage.toAddress,
            thread.latestMessage.bodyText,
        ].filter(Boolean).join(" ").toLocaleLowerCase();
        if (!searchable.includes(search)) return false;
    }

    const quickFilter = typeof queryKey[5] === "string" ? queryKey[5] : "all";
    if (quickFilter === "unread") return thread.unread;
    if (quickFilter.startsWith("product:")) return thread.latestMessage.productKey === quickFilter.slice("product:".length);
    // Recipient-role filters depend on the account's complete alias set. Keep
    // an already-visible row current, but do not guess membership for a new one.
    if ((quickFilter === "to-me" || quickFilter === "cc-me") && !alreadyPresent) return false;
    return true;
}

/**
 * Insert newly shared Gmail messages into the exact conversation and every
 * already-loaded mailbox projection. This is intentionally a cache write, not
 * an invalidation: the imported thread appears before the network request
 * completes and unrelated pages keep their current data.
 */
export function upsertMailboxMessages(queryClient: QueryClient, messages: readonly Message[]): void {
    if (!messages.length) return;
    const threadKey = messages[0].threadKey;
    if (messages.some(message => message.threadKey !== threadKey)) {
        throw new Error("Imported mailbox messages must belong to one thread.");
    }

    const known = knownMessageIds(queryClient, threadKey);
    const fresh = messages.filter(message => !known.has(message.id));

    queryClient.setQueryData<Message[]>(MESSAGES_QUERY_KEY, current =>
        current ? mergeMessages(current, messages) : current,
    );
    queryClient.setQueryData<InfiniteData<Paged<Message>>>(
        [...MESSAGES_QUERY_KEY, "thread", threadKey],
        current => {
            if (!current) {
                return {
                    pages: [{ data: mergeMessages([], messages), pagination: { nextCursor: null } }],
                    pageParams: [undefined],
                };
            }
            return {
                ...current,
                pages: current.pages.map((page, index) => index === 0
                    ? { ...page, data: mergeMessages(page.data, messages) }
                    : page),
            };
        },
    );

    const caches = queryClient.getQueriesData<InfiniteData<MailboxThreadsResponse>>({
        queryKey: MAILBOX_THREADS_QUERY_KEY,
    });
    let existing: MailboxThread | undefined;
    for (const [, current] of caches) {
        existing ??= current?.pages.flatMap(page => page.threads).find(thread => thread.threadKey === threadKey);
    }
    const { thread: projected, newInbound } = projectImportedThread(existing, messages, fresh);

    for (const [queryKey, current] of caches) {
        if (!current) continue;
        const wasPresent = current.pages.some(page => page.threads.some(thread => thread.threadKey === threadKey));
        const shouldAppear = matchesLoadedMailbox(queryKey, projected, wasPresent);
        const flattened = current.pages.flatMap(page => page.threads).filter(thread => thread.threadKey !== threadKey);
        if (shouldAppear) flattened.push(projected);
        flattened.sort((left, right) =>
            right.activityAt.localeCompare(left.activityAt) || right.threadKey.localeCompare(left.threadKey),
        );

        let offset = 0;
        const pages = current.pages.map((page, index) => {
            let capacity = page.threads.length;
            if (!wasPresent && shouldAppear && index === current.pages.length - 1 && !page.pagination.nextCursor) {
                capacity = Math.min(25, capacity + 1);
            }
            const threads = flattened.slice(offset, offset + capacity);
            offset += capacity;
            return { ...page, threads };
        });
        queryClient.setQueryData(queryKey, { ...current, pages });
    }

    if (newInbound && !existing?.unread && !existing?.trashed) {
        queryClient.setQueryData<MailboxSummary>(MAILBOX_SUMMARY_QUERY_KEY, current =>
            current ? { ...current, inboxUnread: current.inboxUnread + 1 } : current,
        );
    }
}

/**
 * Swap one cached message for another everywhere it appears — an optimistic
 * row for the server's row, or a partial realtime row for the full one.
 */
export function replaceMailboxMessage(queryClient: QueryClient, previousId: string, message: Message): void {
    const swap = (current: Message): Message => current.id === previousId || current.id === message.id
        ? { ...current, ...message }
        : current;
    const dedupe = (list: Message[]): Message[] => {
        const seen = new Set<string>();
        return list.map(swap).filter(item => (seen.has(item.id) ? false : (seen.add(item.id), true)));
    };
    queryClient.setQueryData<Message[]>(MESSAGES_QUERY_KEY, current => current ? dedupe(current) : current);
    queryClient.setQueriesData<InfiniteData<Paged<Message>>>(
        { queryKey: [...MESSAGES_QUERY_KEY, "thread"] },
        current => current ? {
            ...current,
            pages: current.pages.map(page => ({ ...page, data: dedupe(page.data) })),
        } : current,
    );
    queryClient.setQueriesData<InfiniteData<MailboxThreadsResponse>>(
        { queryKey: MAILBOX_THREADS_QUERY_KEY },
        current => current ? {
            ...current,
            pages: current.pages.map(page => ({
                ...page,
                threads: page.threads.map(thread => ({ ...thread, latestMessage: swap(thread.latestMessage) })),
            })),
        } : current,
    );
}

/** Drop a message (a draft, or a failed optimistic send) from every cache. */
export function removeMailboxMessage(queryClient: QueryClient, id: string): void {
    queryClient.setQueryData<Message[]>(MESSAGES_QUERY_KEY, current => current?.filter(message => message.id !== id));
    queryClient.setQueriesData<InfiniteData<Paged<Message>>>(
        { queryKey: [...MESSAGES_QUERY_KEY, "thread"] },
        current => current ? {
            ...current,
            pages: current.pages.map(page => ({ ...page, data: page.data.filter(message => message.id !== id) })),
        } : current,
    );
}

/** Snapshot every mailbox cache so an optimistic write can be rolled back exactly. */
export function snapshotMailbox(queryClient: QueryClient): () => void {
    const entries = [
        ...queryClient.getQueriesData({ queryKey: MESSAGES_QUERY_KEY }),
        ...queryClient.getQueriesData({ queryKey: MAILBOX_THREADS_QUERY_KEY }),
        ...queryClient.getQueriesData({ queryKey: MAILBOX_SUMMARY_QUERY_KEY }),
        ...queryClient.getQueriesData({ queryKey: MAILBOX_LABELS_QUERY_KEY }),
    ];
    return () => { for (const [key, data] of entries) queryClient.setQueryData(key, data); };
}

/** Add/remove/replace labels on threads in every loaded view. */
export function patchMailboxThreadLabels(
    queryClient: QueryClient,
    operation: "add" | "remove" | "replace",
    threadKeys: readonly string[],
    labelIds: readonly string[],
): void {
    const affected = new Set(threadKeys);
    const library = queryClient.getQueryData<{ ok: true; labels: MailLabel[] }>(MAILBOX_LABELS_QUERY_KEY)?.labels ?? [];
    const byId = new Map(library.map(label => [label.id, label]));
    const ids = new Set(labelIds);
    const apply = (thread: MailboxThread): MailboxThread => {
        if (!affected.has(thread.threadKey)) return thread;
        let labels = thread.labels;
        if (operation === "replace") labels = [];
        if (operation === "remove") labels = labels.filter(label => !ids.has(label.id));
        else {
            const have = new Set(labels.map(label => label.id));
            labels = [...labels, ...labelIds.filter(id => !have.has(id)).map(id => byId.get(id)).filter((label): label is MailLabel => Boolean(label))];
        }
        return { ...thread, labels };
    };
    for (const [queryKey, current] of queryClient.getQueriesData<InfiniteData<MailboxThreadsResponse>>({ queryKey: MAILBOX_THREADS_QUERY_KEY })) {
        if (!current) continue;
        const labelView = current.pages[0]?.view === "label" && typeof queryKey[3] === "string" ? queryKey[3] : null;
        queryClient.setQueryData(queryKey, {
            ...current,
            pages: current.pages.map(page => ({
                ...page,
                threads: page.threads.map(apply)
                    // Removing the label you're viewing takes the thread out of that view.
                    .filter(thread => !labelView || thread.labels.some(label => label.id === labelView)),
            })),
        });
    }
}

/** Label library create/update/delete, plus the label chips already on threads. */
export function patchMailboxLabel(queryClient: QueryClient, change: { label: MailLabel } | { deletedId: string }): void {
    queryClient.setQueryData<{ ok: true; labels: MailLabel[] }>(MAILBOX_LABELS_QUERY_KEY, current => {
        if (!current) return current;
        if ("deletedId" in change) return { ...current, labels: current.labels.filter(label => label.id !== change.deletedId) };
        const exists = current.labels.some(label => label.id === change.label.id);
        const labels = exists
            ? current.labels.map(label => label.id === change.label.id ? { ...label, ...change.label } : label)
            : [...current.labels, change.label];
        return { ...current, labels: [...labels].sort((a, b) => a.position - b.position) };
    });
    queryClient.setQueriesData<InfiniteData<MailboxThreadsResponse>>(
        { queryKey: MAILBOX_THREADS_QUERY_KEY },
        current => current ? {
            ...current,
            pages: current.pages.map(page => ({
                ...page,
                threads: page.threads.map(thread => ({
                    ...thread,
                    labels: "deletedId" in change
                        ? thread.labels.filter(label => label.id !== change.deletedId)
                        : thread.labels.map(label => label.id === change.label.id ? { ...label, ...change.label } : label),
                })),
            })),
        } : current,
    );
}

/** Replace temporary optimistic ids with the ids returned by the share action. */
export function reconcileMailboxMessageIds(queryClient: QueryClient, ids: ReadonlyMap<string, string>): void {
    if (!ids.size) return;
    const replace = (message: Message): Message => {
        const id = ids.get(message.id);
        return id ? { ...message, id } : message;
    };
    queryClient.setQueryData<Message[]>(MESSAGES_QUERY_KEY, current => current?.map(replace));
    queryClient.setQueriesData<InfiniteData<Paged<Message>>>(
        { queryKey: [...MESSAGES_QUERY_KEY, "thread"] },
        current => current ? {
            ...current,
            pages: current.pages.map(page => ({ ...page, data: page.data.map(replace) })),
        } : current,
    );
    queryClient.setQueriesData<InfiniteData<MailboxThreadsResponse>>(
        { queryKey: MAILBOX_THREADS_QUERY_KEY },
        current => current ? {
            ...current,
            pages: current.pages.map(page => ({
                ...page,
                threads: page.threads.map(thread => ({ ...thread, latestMessage: replace(thread.latestMessage) })),
            })),
        } : current,
    );
}

/** Apply a mailbox mutation to every loaded view without invalidating/refetching. */
export function patchMailboxThreadState(
    queryClient: QueryClient,
    operation: MailboxStateOperation,
    threadKeys: readonly string[],
): void {
    const affected = new Set(threadKeys);
    const caches = queryClient.getQueriesData<InfiniteData<MailboxThreadsResponse>>({
        queryKey: MAILBOX_THREADS_QUERY_KEY,
    });
    const sourceThreads = new Map<string, MailboxThread>();
    for (const [, current] of caches) {
        for (const page of current?.pages ?? []) {
            for (const thread of page.threads) {
                if (affected.has(thread.threadKey)) sourceThreads.set(thread.threadKey, patchThread(thread, operation));
            }
        }
    }

    for (const [queryKey, current] of caches) {
        if (!current) continue;
        const labelId = typeof queryKey[3] === "string" ? queryKey[3] : undefined;
        const search = typeof queryKey[4] === "string" ? queryKey[4].trim().toLocaleLowerCase() : "";
        const pages = current.pages.map(page => ({
            ...page,
            threads: page.threads
                .map(thread => affected.has(thread.threadKey) ? patchThread(thread, operation) : thread)
                .filter(thread => belongsInView(thread, page.view)),
        }));

        const first = pages[0];
        if (first && !search) {
            const present = new Set(pages.flatMap(page => page.threads.map(thread => thread.threadKey)));
            const additions = [...sourceThreads.values()].filter(thread =>
                !present.has(thread.threadKey)
                && belongsInView(thread, first.view)
                && (first.view !== "label" || thread.labels.some(label => label.id === labelId)),
            );
            if (additions.length) {
                first.threads = [...first.threads, ...additions].sort((left, right) =>
                    right.activityAt.localeCompare(left.activityAt) || right.threadKey.localeCompare(left.threadKey),
                );
            }
        }
        queryClient.setQueryData(queryKey, { ...current, pages });
    }
}

/** Mutation responses carry the authoritative total, avoiding a summary GET. */
export function setMailboxUnreadTotal(queryClient: QueryClient, inboxUnread: number): void {
    queryClient.setQueryData<MailboxSummary>(MAILBOX_SUMMARY_QUERY_KEY, {
        ok: true,
        inboxUnread,
    });
}

/** The cached message carrying a given RFC Message-ID header (a reply's parent). */
export function findCachedMessageByHeader(queryClient: QueryClient, messageIdHeader: string): Message | undefined {
    for (const [, data] of queryClient.getQueriesData<Message[] | InfiniteData<Paged<Message>> | InfiniteData<MailboxThreadsResponse>>({ queryKey: MESSAGES_QUERY_KEY })) {
        const list = Array.isArray(data) ? data : (data as InfiniteData<Paged<Message>> | undefined)?.pages?.flatMap(page => page.data ?? []) ?? [];
        const hit = list.find(message => message.messageIdHeader === messageIdHeader);
        if (hit) return hit;
    }
    for (const [, data] of queryClient.getQueriesData<InfiniteData<MailboxThreadsResponse>>({ queryKey: MAILBOX_THREADS_QUERY_KEY })) {
        for (const page of data?.pages ?? []) {
            const hit = page.threads.find(thread => thread.latestMessage.messageIdHeader === messageIdHeader);
            if (hit) return hit.latestMessage;
        }
    }
    return undefined;
}

/** Remove a thread (e.g. an optimistic placeholder) from every loaded mailbox view. */
export function removeMailboxThread(queryClient: QueryClient, threadKey: string): void {
    queryClient.setQueriesData<InfiniteData<MailboxThreadsResponse>>(
        { queryKey: MAILBOX_THREADS_QUERY_KEY },
        current => current ? {
            ...current,
            pages: current.pages.map(page => ({ ...page, threads: page.threads.filter(thread => thread.threadKey !== threadKey) })),
        } : current,
    );
    queryClient.removeQueries({ queryKey: [...MESSAGES_QUERY_KEY, "thread", threadKey], exact: true });
}
